import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { DsButtonModule, DsIconModule, DsTagModule, DsTooltipDirectiveModule } from '@bmw-ds/components';

import { CustomerVehicleRelationType } from '../../../customer/model/customer-vehicle.model';
import { useI18n } from '../../../i18n/services/i18n.service';
import { RelationButtonConfig } from './relation-button-config';

@Component({
  selector: 'cdf-customer-vehicle-relations',
  imports: [DsIconModule, DsTagModule, DsButtonModule, DsTooltipDirectiveModule],
  template: `
    @for (relation of sortedTypes(); track relation) {
      <div
        class="relation-container existing"
        [class.dismissable]="removeButtonConfig()"
        [class.destructive]="relation === markRelationDestructive()"
      >
        <ds-icon class="col" icon="user" size="xs" />
        <span class="col label"
          >{{ i18n.enum(relation, 'customerVehicleRelation.enum') }}
          @if (truncate() && relationTypes().length > 1) {
            <span> +{{ relationTypes().length - 1 }}</span>
          }
        </span>
        @if (removeButtonConfig(); as removeButton) {
          <button
            ds-button
            icon="close"
            size="sm"
            variant="ghost-destructive"
            [disabled]="removeButton.disabled"
            [ds-tooltip]="removeButton.tooltip"
            (click)="removeRelation.emit(relation)"
          ></button>
        }
      </div>
    }
    @if (addButtonConfig(); as addButton) {
      <div class="relation-container add">
        <button
          ds-button
          icon="add"
          size="sm"
          variant="ghost"
          [disabled]="addButton.disabled"
          [ds-tooltip]="addButton.tooltip"
          (click)="addRelation.emit()"
        ></button>
      </div>
    }
  `,
  styleUrl: './customer-vehicle-relations.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerVehicleRelationsComponent {
  readonly i18n = useI18n();

  readonly truncate = input(false, { transform: booleanAttribute });
  readonly relationTypes = input.required({ transform: transformToRelationTypesArray });
  readonly removeButton = input<RelationButtonConfig>();
  readonly addButton = input<RelationButtonConfig>();
  readonly markRelationDestructive = input<CustomerVehicleRelationType | undefined>(undefined);

  readonly removeRelation = output<CustomerVehicleRelationType>();
  readonly addRelation = output();

  readonly sortedTypes = computed<CustomerVehicleRelationType[]>(() => {
    const sorted = sortVehicleRelationTypes(this.relationTypes());
    return this.truncate() ? sorted.slice(0, 1) : sorted;
  });

  protected readonly removeButtonConfig = computed(() =>
    this.buttonConfig(this.removeButton(), this.relationTypes().length),
  );
  protected readonly addButtonConfig = computed(() => this.buttonConfig(this.addButton(), this.relationTypes().length));

  private buttonConfig(button: RelationButtonConfig | undefined, count: number) {
    if (!button) {
      return;
    } else if (button === true) {
      return { disabled: false, tooltip: '' };
    }
    const disabled =
      (button.min !== undefined && count < button.min) || (button.max !== undefined && count > button.max);
    const tooltip = disabled ? button.tooltip : '';
    return { disabled, tooltip };
  }
}

function transformToRelationTypesArray(
  t: ReadonlySet<CustomerVehicleRelationType> | CustomerVehicleRelationType[] | undefined,
): CustomerVehicleRelationType[] {
  return Array.isArray(t) ? t : Array.from(t ?? []);
}

function sortVehicleRelationTypes(relationTypes: CustomerVehicleRelationType[]): CustomerVehicleRelationType[] {
  const priorityOrder = [
    CustomerVehicleRelationType.OWNER,
    CustomerVehicleRelationType.KEEPER,
    CustomerVehicleRelationType.USER,
  ];
  return relationTypes.sort((a, b) => priorityOrder.indexOf(a) - priorityOrder.indexOf(b));
}
